// Library-free location highlight. Draws the region (all counties, baked at
// generate time) as a backdrop and highlights the user's selected area:
//   - county → fill the baked county path matched by FIPS
//   - city / custom → overlay the server's simplified GeoJSON, projected with
//     the same baked projection so it lines up exactly with the backdrop.
//
// The stats cards (area + population) are the non-map data alternative, so
// this SVG is purely supplementary; it is exposed to assistive tech as a
// single labelled image rather than an interactive map.
import {
  useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { regionCountiesSvg } from '../../utils/location-highlight/region-counties-svg.js';
import {
  geometryToSvgPath, svgPathBounds, mergeBounds,
} from '../../utils/location-highlight/project-geojson.js';
import {
  PARTICIPATION_STATUS_ORDER,
  PARTICIPATION_STATUS_LABELS,
  DEFAULT_PARTICIPATION_STATUS,
  participationLabel,
} from '../../config/participation.js';

const { viewBox, projection, counties } = regionCountiesSvg;

// When an area is selected we zoom the SVG camera to a padded box around the
// selection + intersecting tiles instead of regenerating any geometry: the
// highlight and tile paths are already in the baked projection's coordinate
// space, so we just narrow the viewBox. Padding is a share of the larger side
// (with a small floor) so the selection never sits flush against the edge.
const ZOOM_PAD_RATIO = 0.14;
const ZOOM_MIN_PAD = 6;
const round2 = (n) => Math.round(n * 100) / 100;

const computeViewBox = (highlightPath, tileGridPath) => {
  const bounds = mergeBounds(svgPathBounds(highlightPath), svgPathBounds(tileGridPath));
  if (!bounds) return viewBox;
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  const pad = Math.max(Math.max(width, height) * ZOOM_PAD_RATIO, ZOOM_MIN_PAD);
  const x = bounds.minX - pad;
  const y = bounds.minY - pad;
  const w = width + pad * 2;
  const h = height + pad * 2;
  return `${round2(x)} ${round2(y)} ${round2(w)} ${round2(h)}`;
};

// Path styling pulled from the theme so the map shares the active mode's
// palette. Backdrop counties sit on the deepest bg as contrasting "land"
// with a crisp outline so the state reads at a glance; the highlight is the
// teal accent (no stroke). The svg's aria-label names the highlighted area,
// and the stats cards are the non-map data alternative, so the selection isn't
// conveyed by color alone.
const MapSvg = styled('svg')(({ theme }) => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  maxHeight: 420,
  '& .county': {
    fill: theme.mapColors.land,
    stroke: theme.mapColors.landStroke,
    strokeWidth: 0.75,
    vectorEffect: 'non-scaling-stroke',
    cursor: 'default',
    transition: 'filter 0.1s ease',
  },
  // Subtle, theme-agnostic emphasis so a hovered county reads against both the
  // dark and light land fills without introducing a new palette token.
  '& .county:hover': {
    filter: 'brightness(1.18)',
  },
  // Participation shading: only the two "engaged" statuses tint a county; the
  // default (interest-not-expressed) keeps the plain land fill above. The
  // attribute-qualified selector outranks the bare `.county` fill regardless of
  // rule order, and only `fill` is overridden so the shared stroke, hover
  // brightness, and non-scaling stroke all carry over.
  '& .county[data-status="active_quote"]': {
    fill: theme.participationColors.active_quote,
  },
  '& .county[data-status="committed"]': {
    fill: theme.participationColors.committed,
  },
  '& .highlight': {
    fill: theme.mapColors.highlight,
    // Slightly translucent so the land underneath and the tile-grid lines on
    // top both stay legible through the shaded selection.
    fillOpacity: 0.55,
    // A thin same-family stroke fattens small geometries (cities, custom
    // uploads) by a pixel so they register even at map scale.
    stroke: theme.mapColors.highlightStroke,
    strokeWidth: 1.25,
    vectorEffect: 'non-scaling-stroke',
    strokeLinejoin: 'round',
  },
  '& .tile-grid': {
    fill: 'none',
    stroke: theme.mapColors.tileGrid,
    strokeWidth: 1,
    vectorEffect: 'non-scaling-stroke',
    opacity: 0.95,
    pointerEvents: 'none',
  },
}));

const buildHighlightPath = ({ areaType, countyFips, displayGeoJson }) => {
  if (areaType === 'county' && countyFips) {
    const match = counties.find((c) => c.fips === countyFips);
    if (match) return match.path;
  }
  if (displayGeoJson) return geometryToSvgPath(displayGeoJson, projection);
  return '';
};

const describe = ({
  name, hasHighlight, hasTileGrid, loading, regionName, participationCounts,
}) => {
  const region = regionName || 'the region';
  const base = (() => {
    if (loading) return `Map of ${region}. Calculating the selected area…`;
    if (hasHighlight && hasTileGrid) {
      return `Map of ${region} with ${name || 'the selected area'} highlighted and the intersecting tile grid shown.`;
    }
    if (hasHighlight) {
      return `Map of ${region} with ${name || 'the selected area'} highlighted.`;
    }
    return `Map of ${region}. No area selected yet.`;
  })();
  // Convey the color-coded participation overlay non-visually (the legend is the
  // sighted equivalent). Only mention it when at least one county is engaged.
  if (participationCounts) {
    const active = participationCounts.active_quote || 0;
    const committed = participationCounts.committed || 0;
    if (active || committed) {
      return `${base} Participation overlay: ${active} with an active quote, ${committed} committed.`;
    }
  }
  return base;
};

// Legend swatches are tiny inline SVGs so they mirror exactly what's drawn on
// the map: a translucent fill bordered by the highlight stroke for the
// selection, and an outlined grid cell for the intersecting tiles. They are
// decorative (the adjacent text is the real label), hence aria-hidden.
const SelectedAreaSwatch = () => (
  <Box
    aria-hidden="true"
    component="svg"
    viewBox="0 0 16 16"
    sx={(theme) => ({
      width: 16,
      height: 16,
      flexShrink: 0,
      '& rect': {
        fill: alpha(theme.mapColors.highlight, 0.55),
        stroke: theme.mapColors.highlightStroke,
        strokeWidth: 1.25,
      },
    })}
  >
    <rect x="1" y="1" width="14" height="14" rx="2" />
  </Box>
);

const TileGridSwatch = () => (
  <Box
    aria-hidden="true"
    component="svg"
    viewBox="0 0 16 16"
    sx={(theme) => ({
      width: 16,
      height: 16,
      flexShrink: 0,
      '& rect': { fill: 'none', stroke: theme.mapColors.tileGrid, strokeWidth: 1.25 },
      '& line': { stroke: theme.mapColors.tileGrid, strokeWidth: 1 },
    })}
  >
    <rect x="1" y="1" width="14" height="14" rx="1" />
    <line x1="8" y1="1" x2="8" y2="15" />
    <line x1="1" y1="8" x2="15" y2="8" />
  </Box>
);

// Participation swatch: a solid cell that mirrors a county's fill on the map.
// The default (interest-not-expressed) status shows the plain land color so the
// legend explains exactly what an un-shaded county means.
const ParticipationSwatch = ({ status }) => (
  <Box
    aria-hidden="true"
    component="svg"
    viewBox="0 0 16 16"
    sx={(theme) => ({
      width: 16,
      height: 16,
      flexShrink: 0,
      '& rect': {
        fill: status === DEFAULT_PARTICIPATION_STATUS
          ? theme.mapColors.land
          : theme.participationColors[status],
        stroke: theme.mapColors.landStroke,
        strokeWidth: 1,
      },
    })}
  >
    <rect x="1" y="1" width="14" height="14" rx="2" />
  </Box>
);

const LegendItem = ({ swatch, children }) => (
  <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    {swatch}
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
      {children}
    </Typography>
  </Box>
);

const legendListSx = {
  listStyle: 'none',
  m: 0,
  px: 0.5,
  py: 0,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 0.5,
};

// The legend has two groups: the participation key (only rendered when the
// parent opts in via `showParticipation`, always showing all three statuses so
// the un-shaded default is explained) and the selection/tile key (rendered only
// when one of those overlays is present, keeping the user's current pick
// visually primary).
const MapLegend = ({
  hasHighlight, hasTileGrid, name, showParticipation,
}) => {
  const hasSelectionKey = hasHighlight || hasTileGrid;
  if (!showParticipation && !hasSelectionKey) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {showParticipation && (
        <Box component="ul" sx={legendListSx} aria-label="Participation key">
          {PARTICIPATION_STATUS_ORDER.map((status) => (
            <LegendItem key={status} swatch={<ParticipationSwatch status={status} />}>
              {PARTICIPATION_STATUS_LABELS[status]}
            </LegendItem>
          ))}
        </Box>
      )}
      {hasSelectionKey && (
        <Box component="ul" sx={legendListSx}>
          {hasHighlight && (
            <LegendItem swatch={<SelectedAreaSwatch />}>{name || 'Selected area'}</LegendItem>
          )}
          {hasTileGrid && <LegendItem swatch={<TileGridSwatch />}>Intersecting tiles</LegendItem>}
        </Box>
      )}
    </Box>
  );
};

const LocationHighlightSvg = ({
  areaType, countyFips, displayGeoJson, tileGeoJson, name,
  loading = false, regionName, fill = false,
  participation = null, showParticipation = false,
}) => {
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });

  const highlightPath = useMemo(
    () => buildHighlightPath({ areaType, countyFips, displayGeoJson }),
    [areaType, countyFips, displayGeoJson],
  );
  const tileGridPath = useMemo(
    () => (tileGeoJson ? geometryToSvgPath(tileGeoJson, projection) : ''),
    [tileGeoJson],
  );
  const hasHighlight = highlightPath.length > 0;
  const hasTileGrid = tileGridPath.length > 0;
  const activeViewBox = useMemo(
    () => computeViewBox(highlightPath, tileGridPath),
    [highlightPath, tileGridPath],
  );
  // Counts of the two engaged statuses, only when the overlay is enabled, so the
  // aria-label can summarize the coloring. Keyed on the map alone — colors live
  // in CSS, so the theme is not a dependency.
  const participationCounts = useMemo(() => {
    if (!showParticipation || !participation) return null;
    const counts = { active_quote: 0, committed: 0 };
    Object.values(participation).forEach((s) => {
      if (counts[s] != null) counts[s] += 1;
    });
    return counts;
  }, [showParticipation, participation]);
  const label = describe({
    name, hasHighlight, hasTileGrid, loading, regionName, participationCounts,
  });

  // One pointer handler for the whole county layer: the event target is the
  // hovered <path>, whose name we stash in data-county (and, when the
  // participation overlay is on, its status label in data-status-label). We track
  // the cursor in viewport coordinates because the label is rendered in a portal
  // (see below) rather than inside the clipped map container. This stays a purely
  // visual enhancement — the SVG remains a single labelled image for AT.
  const handlePointerMove = (event) => {
    const { target } = event;
    const county = target?.getAttribute?.('data-county');
    if (!county) {
      if (hover) setHover(null);
      return;
    }
    setHover({
      name: county,
      statusLabel: target.getAttribute('data-status-label'),
      x: event.clientX,
      y: event.clientY,
    });
  };
  const clearHover = () => setHover(null);

  // The tooltip lives in a portal on <body> with position: fixed, so it can
  // never be clipped by the map's container nor inflate an ancestor's scroll
  // size (which previously produced a phantom scrollbar near the edges). Here we
  // measure the rendered label and clamp it inside the viewport, flipping it
  // below the cursor when there isn't room above. useLayoutEffect runs before
  // paint, so the clamp is applied without a visible jump.
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!hover || !el) return;
    const margin = 8;
    const { width, height } = el.getBoundingClientRect();
    const left = Math.min(
      Math.max(hover.x - width / 2, margin),
      window.innerWidth - width - margin,
    );
    const above = hover.y - height - 10;
    const top = above >= margin ? above : hover.y + 16;
    setTooltipPos({ left, top });
  }, [hover]);

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1, ...(fill && { flex: 1, minHeight: 0 }),
    }}
    >
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          bgcolor: (theme) => theme.mapColors.backdrop,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 1,
          boxSizing: 'border-box',
          ...(fill && { flex: 1, minHeight: 0 }),
          boxShadow: (theme) => (theme.palette.mode === 'dark'
            ? 'inset 0 1px 2px rgba(0,0,0,0.5)'
            : 'inset 0 1px 2px rgba(15,23,42,0.12)'),
        }}
      >
        <MapSvg
          viewBox={activeViewBox}
          role="img"
          aria-label={label}
          preserveAspectRatio="xMidYMid meet"
          sx={fill ? { height: '100%', maxHeight: 'none' } : undefined}
        >
          <g onMouseMove={handlePointerMove} onMouseLeave={clearHover}>
            {counties.map((county) => {
              const rawStatus = (participation && participation[county.fips])
                || DEFAULT_PARTICIPATION_STATUS;
              // The selected county is left at its plain land fill so the
              // highlight overlay reads exactly as it does today; only the two
              // engaged statuses tint the rest of the map.
              const isSelectedCounty = areaType === 'county' && county.fips === countyFips;
              const fillStatus = showParticipation
                && !isSelectedCounty
                && rawStatus !== DEFAULT_PARTICIPATION_STATUS
                ? rawStatus
                : undefined;
              return (
                <path
                  key={county.fips}
                  className="county"
                  data-county={county.name}
                  data-status-label={showParticipation ? participationLabel(rawStatus) : undefined}
                  data-status={fillStatus}
                  d={county.path}
                />
              );
            })}
          </g>
          {hasHighlight && <path className="highlight" d={highlightPath} style={{ pointerEvents: 'none' }} />}
          {hasTileGrid && <path className="tile-grid" d={tileGridPath} />}
        </MapSvg>
        {hover && createPortal(
          <Box
            ref={tooltipRef}
            aria-hidden="true"
            sx={{
              position: 'fixed',
              top: tooltipPos.top,
              left: tooltipPos.left,
              pointerEvents: 'none',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'rgba(33, 33, 33, 0.92)',
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 600,
              lineHeight: 1.3,
              maxWidth: 220,
              textAlign: 'center',
              zIndex: (theme) => theme.zIndex.tooltip,
              boxShadow: '0 4px 12px -4px rgba(0,0,0,0.5)',
            }}
          >
            <Box component="span" sx={{ display: 'block' }}>{hover.name}</Box>
            {hover.statusLabel && (
              <Box component="span" sx={{ display: 'block', fontWeight: 400, opacity: 0.85 }}>
                {hover.statusLabel}
              </Box>
            )}
          </Box>,
          document.body,
        )}
        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
          }}
          >
            <CircularProgress size={28} aria-hidden="true" />
          </Box>
        )}
      </Box>
      <MapLegend
        hasHighlight={hasHighlight}
        hasTileGrid={hasTileGrid}
        name={name}
        showParticipation={showParticipation}
      />
    </Box>
  );
};

export default LocationHighlightSvg;
